import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, Loader, ChevronDown, Maximize2, AlertCircle, AlertTriangle, AlertOctagon } from "lucide-react";
import { getNodeColor } from "./commonUtils";
import { HealthCountBadges } from "./HealthCountBadges";

const metricsResponseCache = {};

export const MetricsPopover = ({
  visible,
  nodeId,
  nodeName,
  nodeStatus,
  isDataPulseProduct,
  graphProductIds = new Set(),
  position: _position,
  onClose,
}) => {
  const [metrics, setMetrics] = useState(null);
  const [metricsForNodeId, setMetricsForNodeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedByParent, setExpandedByParent] = useState({});
  const [expandedChildren, setExpandedChildren] = useState({});
  const latestRequestRef = useRef(0);
  const activeAbortRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setMetricsForNodeId(null);
    setError(null);

    if (!isDataPulseProduct || !nodeId) {
      setMetrics(null);
      setLoading(false);
      setError(null);
      return;
    }

    fetchMetrics(nodeId);
  }, [visible, nodeId, isDataPulseProduct]);

  useEffect(() => {
    setExpandedByParent({});
    setExpandedChildren({});
  }, [nodeId]);

  useEffect(() => {
    if (!visible) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, onClose]);

  useEffect(() => {
    return () => {
      if (activeAbortRef.current) {
        activeAbortRef.current.abort();
      }
    };
  }, []);

  // Define all constants and computed values (must be before early return)
  const activeMetrics = metricsForNodeId === nodeId ? metrics : null;
  const directChildrenProductCount =
    activeMetrics?.directChildrenProductCount ??
    activeMetrics?.directChildrenCount ??
    0;
  const childProductSubscribedAssets =
    activeMetrics?.childProductSubscribedAssets ??
    activeMetrics?.directChildren ??
    [];
  const directUpstreamProductCount =
    activeMetrics?.directParentProductCount ??
    activeMetrics?.directUpstreamProductCount ??
    activeMetrics?.directParentCount ??
    0;
  const directUpstreamProducts =
    activeMetrics?.directParentProducts ??
    activeMetrics?.directUpstreamProducts ?? activeMetrics?.directParents ?? [];
  const nodeSubscribedAssetCount =
    activeMetrics?.directParentAssetTotalCount ??
    activeMetrics?.nodeSubscribedAssetCount ??
    activeMetrics?.assetsSubscribedByNodeCount ??
    0;
  const totalChildSubscribedAssetCount =
    activeMetrics?.childSubscribedAssetTotalCount ??
    childProductSubscribedAssets.reduce(
      (sum, child) => sum + (child?.subscribedAssetCount ?? 0),
      0,
    );

  // Build child data with "Outside graph" detection from API response
  const childrenWithGraphStatus = useMemo(() => {
    return childProductSubscribedAssets.map((child) => ({
      ...child,
      // Detect if child is "Outside graph" by checking if its product_id is in graphProductIds
      isOutsideGraph: !graphProductIds.has(child.product_id),
    }));
  }, [childProductSubscribedAssets, graphProductIds]);

  // Build parent data with "Outside graph" detection from API response
  const parentsWithGraphStatus = useMemo(() => {
    return directUpstreamProducts.map((parent) => ({
      ...parent,
      // Detect if parent is "Outside graph" by checking if its product_id is in graphProductIds
      isOutsideGraph: !graphProductIds.has(parent.product_id),
    }));
  }, [directUpstreamProducts, graphProductIds]);

  // Check if any parent is problematic
  const anyParentProblematic = useMemo(() => {
    return parentsWithGraphStatus.some((parent) => parent.isProblematic);
  }, [parentsWithGraphStatus]);

  // Check if any child is affected
  const anyChildAffected = useMemo(() => {
    return childrenWithGraphStatus.some((child) => child.isAffected);
  }, [childrenWithGraphStatus]);

  const isMetricsReadyForNode = !isDataPulseProduct || Boolean(activeMetrics);
  const shouldShowLoader =
    isDataPulseProduct && (loading || !isMetricsReadyForNode);
  const productStatusColor = getNodeColor(
    null,
    nodeStatus,
    isDataPulseProduct,
  );

  const totalAssetsSubscribedByNode = nodeSubscribedAssetCount || 0;

  const fetchMetrics = async (targetNodeId) => {
    const requestId = ++latestRequestRef.current;
    const cachedMetrics = metricsResponseCache[targetNodeId];
    const hasLatestChildTotalField =
      cachedMetrics &&
      Object.prototype.hasOwnProperty.call(
        cachedMetrics,
        "childSubscribedAssetTotalCount",
      );

    if (cachedMetrics && hasLatestChildTotalField) {
      if (requestId !== latestRequestRef.current) {
        return;
      }
      setMetrics(cachedMetrics);
      setMetricsForNodeId(targetNodeId);
      setLoading(false);
      setError(null);
      return;
    }

    if (activeAbortRef.current) {
      activeAbortRef.current.abort();
    }
    const abortController = new AbortController();
    activeAbortRef.current = abortController;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/get-node-metrics/${targetNodeId}`,
        {
          signal: abortController.signal,
        },
      );
      if (!response.ok) throw new Error("Failed to fetch metrics");
      const data = await response.json();
      if (requestId !== latestRequestRef.current) {
        return;
      }
      metricsResponseCache[targetNodeId] = data;
      setMetrics(data);
      setMetricsForNodeId(targetNodeId);
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }
      if (requestId !== latestRequestRef.current) {
        return;
      }
      setError(err.message);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };

  if (!visible || !nodeId) return null;

  const renderAssetRows = (assets, groupKey) => {
    return assets.map((asset) => {
      const assetStatus = String(asset?.status || "").toLowerCase();
      const isNonDpAsset =
        !asset?.product_name || assetStatus === "na" || assetStatus === "n/a";
      const statusText = isNonDpAsset ? "NA" : asset?.status;
      const statusColor = isNonDpAsset
        ? "#9CA3AF"
        : getNodeColor(null, asset?.status);

      return (
        <div
          key={`${groupKey}-${asset.data_asset_id}`}
          className="flex items-start gap-1.5"
        >
          <div
            className="mt-0.5 flex-shrink-0 w-1 h-2 rounded-sm"
            style={{ backgroundColor: statusColor }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-gray-700 break-words leading-tight">
              {asset.data_asset_name}
            </div>
            <span
              className="inline-flex w-fit text-[9px] font-medium px-1 py-0 rounded-sm bg-gray-100"
              style={{ color: statusColor }}
            >
              {statusText}
            </span>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {/* Slide Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="metrics-panel-title"
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white border-l border-gray-300 shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3
              id="metrics-panel-title"
              className="text-sm font-semibold text-gray-900"
            >
              {nodeName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Asset Details And Metrics
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Close"
            aria-label="Close metrics panel"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {error ? (
          <div className="text-xs text-red-600 bg-red-50 p-2 rounded m-4">
            {error}
          </div>
        ) : shouldShowLoader ? (
          <div className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="flex flex-col items-center gap-2 text-sm text-gray-500">
              <Loader size={22} className="animate-spin text-indigo-600" />
              <span>Loading metrics...</span>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {isDataPulseProduct === false ? (
              <div className="text-xs text-gray-500 p-3 rounded-lg">
                Metrics are not available because this product is not registered
                in Data Pulse.
              </div>
            ) : activeMetrics ? (
              <>
                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-700">
                      Total Assets Subscribed:
                    </div>
                    <div className="text-2xl font-bold text-green-600 leading-none">
                      {totalAssetsSubscribedByNode}
                    </div>
                  </div>
                  {activeMetrics?.parentAssetHealthCounts && (
                    <HealthCountBadges healthCounts={activeMetrics.parentAssetHealthCounts} />
                  )}
                </div>

                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-gray-700">
                      Direct Parents (Upstream)
                    </div>
                    <div className="text-2xl font-bold text-indigo-700 leading-none">
                      {directUpstreamProductCount}
                    </div>
                  </div>

                  {anyParentProblematic && (
                    <div className="mb-3 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded">
                      <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">
                        Data quality issues detected in upstream providers. Please review.
                      </p>
                    </div>
                  )}

                  {directUpstreamProductCount > 0 ? (
                    <div className="space-y-1 px-1">
                      {parentsWithGraphStatus.map((parent) => (
                        <div
                          key={parent.product_id}
                          className={`border rounded ${
                            parent.isOutsideGraph
                              ? "border-indigo-200 bg-white"
                              : "border-indigo-300 bg-white"
                          }`}
                        >
                          <button
                            type="button"
                            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-indigo-50"
                            onClick={() =>
                              setExpandedByParent((prev) => ({
                                ...prev,
                                [parent.product_id]: !prev[parent.product_id],
                              }))
                            }
                          >
                            <ChevronDown
                              size={14}
                              className={`text-gray-600 transition-transform ${
                                expandedByParent[parent.product_id]
                                  ? "rotate-0"
                                  : "-rotate-90"
                              }`}
                            />
                            <span className="text-xs font-medium truncate text-gray-700">
                              {parent.product_name || parent.product_id}
                            </span>
                            {parent.isProblematic && (
                              <AlertOctagon size={14} className="text-red-600 flex-shrink-0 animate-pulse" />
                            )}
                            <div className="ml-auto flex items-center gap-1">
                              {parent.isOutsideGraph && (
                                <>
                                  <span
                                    className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium"
                                    title="Not part of this graph"
                                  >
                                    Outside graph
                                  </span>
                                  <a
                                    href={`/traffic-light-fullscreen?contractId=${parent.product_id}&viewMode=network`}
                                    target="_self"
                                    className="p-1 text-amber-600 hover:bg-amber-100 rounded transition-colors"
                                    title="View this in full screen (right-click to open in new tab)"
                                  >
                                    <Maximize2 size={14} />
                                  </a>
                                </>
                              )}
                              <span className="text-[10px] text-indigo-700 font-semibold">
                                {parent.subscribedAssetCount ?? 0}
                              </span>
                            </div>
                          </button>

                          {expandedByParent[parent.product_id] &&
                            (parent.subscribedAssets?.length > 0 ? (
                              <div className="border-t border-indigo-200 bg-indigo-50/50 px-2 py-2 max-h-40 overflow-y-auto space-y-2">
                                <div className="space-y-1">
                                  {renderAssetRows(
                                    parent.subscribedAssets,
                                    parent.product_id,
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="border-t border-indigo-200 bg-indigo-50/50 px-2 py-1 text-xs text-gray-500">
                                No assets subscribed from this parent.
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 leading-snug break-words">
                      No direct parents found in metrics response.
                    </div>
                  )}
                </div>

                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-700">
                      Total Assets Consumed By Children:
                    </div>
                    <div className="text-2xl font-bold text-green-600 leading-none">
                      {totalChildSubscribedAssetCount}
                    </div>
                  </div>
                  {activeMetrics?.childAssetHealthCounts && (
                    <HealthCountBadges healthCounts={activeMetrics.childAssetHealthCounts} />
                  )}
                </div>

                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-gray-700">
                      Direct Children (Downstream)
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-bold text-indigo-700 leading-none">
                        {directChildrenProductCount}
                      </div>
                    </div>
                  </div>

                  {anyChildAffected && (
                    <div className="mb-3 flex flex-col gap-1.5">
                      <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <AlertCircle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-yellow-700">
                          Data quality issues in your assets affecting downstream consumers. Please investigate.
                        </p>
                      </div>
                      {activeMetrics?.criticalAssets && activeMetrics.criticalAssets.length > 0 && (
                        <div className="p-1.5 bg-red-50 border border-red-200 rounded">
                          <div className="text-[10px] font-semibold text-red-700 mb-1">Critical Assets:</div>
                          <div className="space-y-1">
                            {activeMetrics.criticalAssets.map((asset) => (
                              <div key={asset.data_asset_id} className="text-[10px] text-red-700 truncate" title={asset.data_asset_name}>
                                • {asset.data_asset_name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {directChildrenProductCount > 0 ? (
                    <div className="space-y-1 px-1">
                      {childrenWithGraphStatus
                        .sort(
                          (a, b) =>
                            (b?.subscribedAssetCount ?? 0) -
                            (a?.subscribedAssetCount ?? 0),
                        )
                        .map((child) => (
                          <div
                            key={child.product_id}
                            className={`border rounded ${
                              child.isOutsideGraph
                                ? "border-indigo-200 bg-white"
                                : "border-indigo-200 bg-white"
                            }`}
                          >
                            <button
                              type="button"
                              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-indigo-50 transition-colors"
                              onClick={() =>
                                setExpandedChildren((prev) => ({
                                  ...prev,
                                  [child.product_id]: !prev[child.product_id],
                                }))
                              }
                            >
                              <ChevronDown
                                size={14}
                                className={`text-gray-600 transition-transform ${
                                  expandedChildren[child.product_id]
                                    ? "rotate-0"
                                    : "-rotate-90"
                                }`}
                              />
                              <span className="text-xs font-medium truncate text-gray-700">
                                {child.product_name || child.product_id}
                              </span>
                              {child.isAffected && (
                                <AlertCircle size={12} className="text-yellow-600 flex-shrink-0" />
                              )}
                              <div className="ml-auto flex items-center gap-1">
                                {child.isOutsideGraph && (
                                  <>
                                    <span
                                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium"
                                      title="Not part of this graph"
                                    >
                                      Outside graph
                                    </span>
                                    <a
                                      href={`/traffic-light-fullscreen?contractId=${child.product_id}&viewMode=network`}
                                      target="_self"
                                      className="p-1 text-amber-600 hover:bg-amber-100 rounded transition-colors"
                                      title="View this in full screen (right-click to open in new tab)"
                                    >
                                      <Maximize2 size={14} />
                                    </a>
                                  </>
                                )}
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                  {child.subscribedAssetCount ?? 0}
                                </span>
                              </div>
                            </button>

                            {expandedChildren[child.product_id] &&
                              (child.subscribedAssets?.length > 0 ? (
                                <div className="border-t border-indigo-200 bg-indigo-50/50 px-2 py-2 max-h-40 overflow-y-auto space-y-2">
                                  <div className="space-y-1">
                                    {renderAssetRows(
                                      child.subscribedAssets,
                                      child.product_id,
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="border-t border-indigo-200 bg-indigo-50/50 px-2 py-1 text-xs text-gray-500">
                                  No assets consumed by this child.
                                </div>
                              ))}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="px-1 text-xs text-gray-500">
                      No direct children found.
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};
